import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

const CLUB_LOGO_SRC = "/picture/LogoClub/UTMRBCLOGO.png";

// ─── Design Tokens ─────────────────────────────────────────────────────────────
const T = {
  bg0: "#02050E",
  bg1: "#060C18",
  bg2: "#0A1220",
  bg3: "#0F1A2E",
  bg4: "#152238",

  p400: "#3B74F5",
  p500: "#2558D4",

  a200: "#6EEEFF",
  a300: "#22D9F0",
  a400: "#00BCD4",

  g300: "#4ADE80",
  r300: "#F87171",
  r400: "#EF4444",
  w300: "#FCD34D",

  t0: "#FFFFFF",
  t1: "#EEF2FA",
  t2: "#B8C5D8",
  t3: "#6B7A96",
  t4: "#3D4A60",

  b1: "rgba(34,217,240,0.06)",
  b2: "rgba(34,217,240,0.12)",
  b3: "rgba(34,217,240,0.22)",
};

// ─── Animated Grid Background ──────────────────────────────────────────────────
function GridBackground() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    let raf, frame = 0;
    const pts = [];
    const N = 28;

    const resize = () => { c.width = c.offsetWidth; c.height = c.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);

    for (let i = 0; i < N; i++) {
      pts.push({
        x: Math.random() * (c.width || 500),
        y: Math.random() * (c.height || 700),
        vx: (Math.random() - 0.5) * 0.18,
        vy: (Math.random() - 0.5) * 0.18,
      });
    }

    const draw = () => {
      frame++;
      const w = c.width, h = c.height;
      ctx.clearRect(0, 0, w, h);

      // Subtle grid
      ctx.strokeStyle = "rgba(0,188,212,0.035)";
      ctx.lineWidth = 0.5;
      for (let x = 0; x < w; x += 55) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke(); }
      for (let y = 0; y < h; y += 55) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke(); }

      // Scan line
      const sy = (frame * 0.25) % h;
      const sg = ctx.createLinearGradient(0, sy - 40, 0, sy + 40);
      sg.addColorStop(0, "rgba(34,217,240,0)");
      sg.addColorStop(0.5, "rgba(34,217,240,0.018)");
      sg.addColorStop(1, "rgba(34,217,240,0)");
      ctx.fillStyle = sg;
      ctx.fillRect(0, sy - 40, w, 80);

      // Nodes
      pts.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > w) p.vx *= -1;
        if (p.y < 0 || p.y > h) p.vy *= -1;
      });
      pts.forEach((a, i) => {
        pts.slice(i + 1).forEach(b => {
          const dx = a.x - b.x, dy = a.y - b.y, d = Math.sqrt(dx*dx + dy*dy);
          if (d < 140) {
            ctx.strokeStyle = `rgba(34,217,240,${(1 - d/140)*0.08})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
          }
        });
        const pulse = 0.3 + 0.4 * Math.abs(Math.sin(frame * 0.012 + i));
        ctx.beginPath(); ctx.arc(a.x, a.y, 1.2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(34,217,240,${pulse})`;
        ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);

  return (
    <canvas ref={canvasRef} style={{ position: "fixed", inset: 0, width: "100%", height: "100%", zIndex: 0, pointerEvents: "none" }} />
  );
}

// ─── Robot Icon ────────────────────────────────────────────────────────────────
function RobotIcon({ glowing }) {
  return (
    <svg viewBox="0 0 56 56" width={44} height={44} style={{ display: "block", filter: glowing ? `drop-shadow(0 0 10px ${T.a300})` : `drop-shadow(0 0 4px ${T.a300}88)`, transition: "filter 0.6s ease" }}>
      <defs>
        <linearGradient id="rg2" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={T.p400} />
          <stop offset="100%" stopColor={T.a300} />
        </linearGradient>
      </defs>
      <rect x="14" y="20" width="28" height="22" rx="5" fill="none" stroke="url(#rg2)" strokeWidth="1.6" />
      <circle cx="22" cy="30" r="3.5" fill={T.a300} opacity="0.9" />
      <circle cx="34" cy="30" r="3.5" fill={T.a300} opacity="0.9" />
      <circle cx="22" cy="30" r="1.4" fill={T.bg0} />
      <circle cx="34" cy="30" r="1.4" fill={T.bg0} />
      <rect x="23" y="37" width="10" height="2" rx="1" fill={T.a300} opacity="0.45" />
      <rect x="26" y="12" width="4" height="8" rx="2" fill="none" stroke={T.a300} strokeWidth="1.4" opacity="0.65" />
      <circle cx="28" cy="11" r="2" fill={T.a300} opacity="0.8" />
      <rect x="6" y="24" width="8" height="3.5" rx="1.75" fill="none" stroke={T.a300} strokeWidth="1.3" opacity="0.5" />
      <rect x="42" y="24" width="8" height="3.5" rx="1.75" fill="none" stroke={T.a300} strokeWidth="1.3" opacity="0.5" />
      <rect x="20" y="42" width="4.5" height="7" rx="2" fill="none" stroke={T.a300} strokeWidth="1.3" opacity="0.5" />
      <rect x="31.5" y="42" width="4.5" height="7" rx="2" fill="none" stroke={T.a300} strokeWidth="1.3" opacity="0.5" />
    </svg>
  );
}

// ─── Field with states ─────────────────────────────────────────────────────────
function CyberField({ label, type, value, onChange, placeholder, error, disabled, icon, endAdornment }) {
  const [focused, setFocused] = useState(false);
  const hasError = !!error;

  const borderColor = hasError
    ? T.r300
    : focused
    ? T.a300
    : value
    ? T.b3
    : T.b2;

  const shadow = hasError
    ? `0 0 0 3px rgba(248,113,113,0.1), 0 0 12px rgba(248,113,113,0.08)`
    : focused
    ? `0 0 0 3px rgba(34,217,240,0.1), 0 0 16px rgba(34,217,240,0.07)`
    : "none";

  return (
    <div style={{ marginBottom: 4 }}>
      <label style={{
        display: "block",
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 11,
        letterSpacing: 2,
        color: hasError ? T.r300 : focused ? T.a300 : T.t3,
        textTransform: "uppercase",
        marginBottom: 7,
        transition: "color 0.2s ease",
        fontWeight: 500,
      }}>
        {label}
      </label>
      <div style={{ position: "relative" }}>
        {icon && (
          <div style={{
            position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)",
            color: hasError ? T.r300 : focused ? T.a300 : T.t4,
            display: "flex", alignItems: "center",
            transition: "color 0.2s ease",
          }}>
            {icon}
          </div>
        )}
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            width: "100%",
            boxSizing: "border-box",
            background: focused ? "rgba(34,217,240,0.04)" : "rgba(34,217,240,0.02)",
            border: `1px solid ${borderColor}`,
            borderRadius: 10,
            color: T.t1,
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 14,
            letterSpacing: 0.4,
            padding: `13px ${endAdornment ? "44px" : "14px"} 13px ${icon ? "40px" : "14px"}`,
            outline: "none",
            boxShadow: shadow,
            transition: "all 0.22s ease",
            caretColor: T.a300,
            opacity: disabled ? 0.5 : 1,
          }}
        />
        {endAdornment && (
          <div style={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)" }}>
            {endAdornment}
          </div>
        )}
      </div>
      {hasError && (
        <p style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 11,
          color: T.r300,
          margin: "6px 0 0 2px",
          letterSpacing: 0.5,
          display: "flex",
          alignItems: "center",
          gap: 5,
        }}>
          <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
            <circle cx="6" cy="6" r="5.25" stroke={T.r300} strokeWidth="1.2" />
            <path d="M6 3.5v3M6 8.5v.5" stroke={T.r300} strokeWidth="1.2" strokeLinecap="round" />
          </svg>
          {error}
        </p>
      )}
    </div>
  );
}

// ─── Corner Brackets ───────────────────────────────────────────────────────────
function Bracket({ pos }) {
  const sz = 14;
  const b = `1.5px solid ${T.a300}44`;
  const base = { position: "absolute", width: sz, height: sz };
  const styles = {
    tl: { top: 8, left: 8, borderTop: b, borderLeft: b },
    tr: { top: 8, right: 8, borderTop: b, borderRight: b },
    bl: { bottom: 8, left: 8, borderBottom: b, borderLeft: b },
    br: { bottom: 8, right: 8, borderBottom: b, borderRight: b },
  };
  return <div style={{ ...base, ...styles[pos] }} />;
}

// ─── Shimmer button bar ────────────────────────────────────────────────────────
const shimmerKf = `@keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
@keyframes spin { to { transform: rotate(360deg); } }
@keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
@keyframes slideDown { from { opacity: 0; max-height: 0; } to { opacity: 1; max-height: 80px; } }
@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
input::placeholder { color: rgba(107,122,150,0.55) !important; font-size: 13px !important; }
* { box-sizing: border-box; }
`;

// ─── Main Login Component ──────────────────────────────────────────────────────
export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [authError, setAuthError] = useState("");
  const [iconGlow, setIconGlow] = useState(false);
  const [pressed, setPressed] = useState(false);

  // Icon pulse
  useEffect(() => {
    const id = setInterval(() => setIconGlow(g => !g), 1800);
    return () => clearInterval(id);
  }, []);

  const validate = () => {
    const e = {};
    if (!email) e.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(email)) e.email = "Invalid email format";
    if (!password) e.password = "Password is required";
    else if (password.length < 8) e.password = "Min. 8 characters";
    return e;
  };

  const handleSubmit = async () => {
    setAuthError("");
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (!result.success) {
      const msg =
        result.fieldErrors?.email?.[0] ||
        (typeof result.error === "string" ? result.error : null) ||
        "Authentication failed — identifier or key mismatch.";
      setAuthError(msg);
      return;
    }
    navigate("/dashboard", { replace: true });
  };

  // ─── Icons ───────────────────────────────────────────────────────────────────
  const EmailIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  );
  const LockIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );
  const EyeIcon = ({ off }) => off ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );
  const outerStyle = {
    minHeight: "100vh",
    background: T.bg0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px 16px",
    fontFamily: "'Outfit', sans-serif",
    position: "relative",
    overflow: "hidden",
  };

  return (
    <div style={outerStyle}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=Outfit:wght@400;600;700;800&display=swap');
        ${shimmerKf}
      `}</style>

      <GridBackground />

      {/* Ambient glow orbs */}
      <div style={{ position: "fixed", top: "10%", left: "8%", width: 300, height: 300, background: `radial-gradient(circle, rgba(59,116,245,0.07) 0%, transparent 70%)`, filter: "blur(50px)", pointerEvents: "none", zIndex: 0 }} />
      <div style={{ position: "fixed", bottom: "15%", right: "10%", width: 250, height: 250, background: `radial-gradient(circle, rgba(34,217,240,0.05) 0%, transparent 70%)`, filter: "blur(50px)", pointerEvents: "none", zIndex: 0 }} />

      {/* ── Navbar ── */}
      <div style={{
        position: "fixed", top: 0, left: 0, right: 0, height: 50,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 20px",
        background: "rgba(6,12,24,0.88)",
        backdropFilter: "blur(24px)",
        borderBottom: `1px solid ${T.b2}`,
        zIndex: 100,
        height: 76,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img
            src={CLUB_LOGO_SRC}
            alt=""
            style={{
              width: 30, height: 30, borderRadius: 8, objectFit: "contain",
              background: "rgba(15,26,46,0.75)", border: `1px solid ${T.b2}`,
              padding: 2, boxShadow: `0 0 16px rgba(34,217,240,0.25)`,  borderRadius: "50%"
            }}
          />
          <div>
            <div style={{ fontFamily: "'Outfit',sans-serif", fontWeight: 800, fontSize: 13, letterSpacing: 3, color: T.t1, textTransform: "uppercase", lineHeight: 1 }}>UTM Robocon</div>
            <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, color: T.a300, letterSpacing: 2, textTransform: "uppercase", opacity: 0.75 }}>Control System</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {[{ label: "SYS", color: T.g300 }, { label: "AUTH", color: T.a300 }].map(({ label, color }) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <div style={{ width: 5, height: 5, borderRadius: "50%", background: color, boxShadow: `0 0 6px ${color}`, animation: "pulse 2.5s ease-in-out infinite" }} />
              <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, color, letterSpacing: 1.5, fontWeight: 600, opacity: 0.8 }}>{label}</span>
            </div>
          ))}
          <div style={{ padding: "3px 10px", borderRadius: 6, border: `1px solid ${T.b2}`, background: T.b1 }}>
            <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, color: T.a300, letterSpacing: 1.5 }}><a href="/">Back</a></span>
          </div>
        </div>
      </div>

      {/* ── Card ── */}
      <div style={{
        position: "relative", zIndex: 1,
        width: "100%", maxWidth: 420,
        marginTop: 50,
        animation: "fadeIn 0.6s ease both",
      }}>
        <div style={{
          borderRadius: 18,
          border: `1px solid ${T.b3}`,
          background: `linear-gradient(145deg, rgba(15,26,46,0.97), rgba(10,18,32,0.95))`,
          backdropFilter: "blur(32px)",
          boxShadow: `0 24px 64px rgba(0,0,0,0.55), 0 0 0 1px rgba(34,217,240,0.04)`,
          overflow: "hidden",
        }}>
          {/* Shimmer bar */}
          <div style={{
            height: 2,
            background: `linear-gradient(90deg, ${T.p400}, ${T.a300}, rgba(74,222,128,0.5), ${T.a300}, ${T.p400})`,
            backgroundSize: "200% 100%",
            animation: "shimmer 3s linear infinite",
          }} />

          {/* Card topbar */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "10px 20px",
            borderBottom: `1px solid ${T.b1}`,
            background: "rgba(34,217,240,0.025)",
          }}>
            <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11, letterSpacing: 2.5, color: T.a300, textTransform: "uppercase", fontWeight: 600 }}>
              auth.terminal
            </span>
            <div style={{ display: "flex", gap: 6 }}>
              {["#FF5F57", "#FFBD2E", "#28C840"].map((c, i) => (
                <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: c, opacity: 0.8 }} />
              ))}
            </div>
          </div>

          {/* Card content */}
          <div style={{ position: "relative", padding: "28px 28px 24px" }}>
            <Bracket pos="tl" /><Bracket pos="tr" />
            <Bracket pos="bl" /><Bracket pos="br" />

            {/* ─ LOGIN VIEW ─ */}
            <div>
                {/* Header */}
                <div style={{ textAlign: "center", marginBottom: 28 }}>
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
                    <RobotIcon glowing={iconGlow} />
                  </div>
                  <h1 style={{ margin: 0, fontFamily: "'Outfit',sans-serif", fontWeight: 800, fontSize: 30, letterSpacing: 6, textTransform: "uppercase", color: T.t1, lineHeight: 1 }}>
                    Sign In
                  </h1>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, justifyContent: "center" }}>
                    <div style={{ flex: 1, height: 1, maxWidth: 60, background: `linear-gradient(90deg, transparent, ${T.b3})` }} />
                    <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, letterSpacing: 2.5, color: T.t3, textTransform: "uppercase" }}>
                      Enter credentials
                    </span>
                    <div style={{ flex: 1, height: 1, maxWidth: 60, background: `linear-gradient(90deg, ${T.b3}, transparent)` }} />
                  </div>
                </div>

                {/* Auth error */}
                {authError && (
                  <div style={{
                    display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px",
                    background: "rgba(248,113,113,0.06)",
                    border: `1px solid rgba(248,113,113,0.25)`,
                    borderRadius: 10, marginBottom: 20,
                    animation: "slideDown 0.25s ease both",
                    overflow: "hidden",
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.r300} strokeWidth="2" strokeLinecap="round" style={{ marginTop: 1, flexShrink: 0 }}>
                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11.5, color: T.r300, lineHeight: 1.6, letterSpacing: 0.3 }}>{authError}</span>
                  </div>
                )}

                {/* Fields */}
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <CyberField
                    label="Email"
                    type="email"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setErrors(er => ({ ...er, email: "" })); setAuthError(""); }}
                    placeholder="user@utm.my"
                    error={errors.email}
                    disabled={loading}
                    icon={<EmailIcon />}
                  />
                  <CyberField
                    label="Password"
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={e => { setPassword(e.target.value); setErrors(er => ({ ...er, password: "" })); setAuthError(""); }}
                    placeholder="••••••••••"
                    error={errors.password}
                    disabled={loading}
                    icon={<LockIcon />}
                    endAdornment={
                      <button
                        onClick={() => setShowPw(!showPw)}
                        tabIndex={-1}
                        style={{
                          background: "none", border: "none", cursor: "pointer",
                          color: T.t4, padding: "8px 10px", borderRadius: 7,
                          display: "flex", alignItems: "center",
                          transition: "color 0.2s ease",
                        }}
                        onMouseEnter={e => e.target.style.color = T.a300}
                        onMouseLeave={e => e.target.style.color = T.t4}
                      >
                        <EyeIcon off={showPw} />
                      </button>
                    }
                  />
                </div>

                {/* Submit */}
                <div style={{ marginTop: 22 }}>
                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    onMouseDown={() => setPressed(true)}
                    onMouseUp={() => setPressed(false)}
                    onMouseLeave={() => setPressed(false)}
                    style={{
                      width: "100%", padding: "15px 0", borderRadius: 11,
                      fontFamily: "'IBM Plex Mono',monospace", fontSize: 14, letterSpacing: 4, fontWeight: 600, textTransform: "uppercase",
                      border: "none", cursor: loading ? "not-allowed" : "pointer",
                      position: "relative", overflow: "hidden",
                      background: loading
                        ? "rgba(34,217,240,0.07)"
                        : `linear-gradient(100deg, ${T.p400} 0%, ${T.a300} 100%)`,
                      color: loading ? T.t3 : T.bg0,
                      boxShadow: loading ? "none" : pressed ? `0 2px 12px rgba(59,116,245,0.3)` : `0 6px 28px rgba(59,116,245,0.45), 0 2px 8px rgba(34,217,240,0.3)`,
                      transform: pressed && !loading ? "scale(0.985)" : "scale(1)",
                      transition: "all 0.18s ease",
                    }}
                  >
                    {/* Shimmer overlay */}
                    {!loading && (
                      <div style={{
                        position: "absolute", inset: 0,
                        background: "linear-gradient(90deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)",
                        backgroundSize: "200% 100%",
                        animation: "shimmer 2.8s linear infinite",
                        borderRadius: 11,
                      }} />
                    )}
                    <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                      {loading ? (
                        <>
                          <div style={{ width: 15, height: 15, border: `2px solid rgba(34,217,240,0.2)`, borderTopColor: T.a300, borderRadius: "50%", animation: "spin 0.85s linear infinite" }} />
                          Signing in...
                        </>
                      ) : (
                        <>
                          Sign In
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                          </svg>
                        </>
                      )}
                    </div>
                  </button>
                </div>
            </div>
          </div>

          {/* Card footer */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "9px 20px",
            borderTop: `1px solid ${T.b1}`,
            background: "rgba(34,217,240,0.015)",
          }}>
            <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9.5, color: T.t4, letterSpacing: 2 }}>
              TLS 1.3 · AES-256-GCM
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 5, height: 5, borderRadius: "50%", background: T.g300, boxShadow: `0 0 6px ${T.g300}`, animation: "pulse 2s ease-in-out infinite" }} />
              <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9.5, color: T.g300, letterSpacing: 1.5, fontWeight: 600 }}>SECURE</span>
            </div>
          </div>
        </div>

        {/* Caption */}
        <div style={{ textAlign: "center", marginTop: 18 }}>
          <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, letterSpacing: 2.5, color: T.t4, textTransform: "uppercase" }}>
            UTM Robocon — Authorised Access Only
          </span>
        </div>
      </div>
    </div>
  );
}